import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { MinimalTiptapEditor } from '@/components/ui/minimal-tiptap/minimal-tiptap'
import ConfirmDialog from 'app/frontend/base/components/ConfirmDialog'
import { useTeam } from 'app/frontend/base/config/context/team'
import { uploadFile } from 'app/frontend/base/api/team'
import Card from './Card'
import { cn } from '@/lib/utils'

export const hasNotesContent = (html: string | undefined | null): boolean => {
  if (!html) return false
  if (/<(img|iframe|video|audio|embed)\b/i.test(html)) return true
  return html.replace(/<[^>]*>/g, '').trim().length > 0
}

type Props = {
  initialValue: string
  onSave: (notes: string) => Promise<void>
  onCancel: () => void
  clearTarget?: string
  footerNote?: string
  className?: string
}

const NotesEditor = ({
  initialValue,
  onSave,
  onCancel,
  clearTarget = 'this event',
  footerNote,
  className,
}: Props) => {
  const { team } = useTeam()
  const uploader = async (file: File) => (await uploadFile(team.id, file)).url
  const [draft, setDraft] = useState(initialValue)
  const [isSaving, setIsSaving] = useState(false)
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false)

  const hasSavedNotes = hasNotesContent(initialValue)

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await onSave(draft)
    } finally {
      setIsSaving(false)
    }
  }

  const handleClear = async () => {
    setIsSaving(true)
    try {
      await onSave('')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      <Card
        className={cn(
          'flex flex-col overflow-hidden p-0 shadow-2xs',
          className,
        )}
      >
        <MinimalTiptapEditor
          value={draft}
          onChange={(value) => setDraft(typeof value === 'string' ? value : '')}
          output='html'
          placeholder='Add message…'
          uploader={uploader}
          autofocus='end'
          className='rounded-none border-0 shadow-none'
          editorContentClassName='min-h-32'
        />
        <div className='flex items-center gap-2 justify-between border-t p-2'>
          {footerNote ? (
            <span className='text-xs text-muted-foreground'>{footerNote}</span>
          ) : null}
          <div className='flex gap-2 ml-auto'>
            {hasSavedNotes ? (
              <Button
                variant='ghost'
                size='sm'
                className='hover:text-destructive-foreground'
                disabled={isSaving}
                onClick={() => setIsClearConfirmOpen(true)}
              >
                Clear
              </Button>
            ) : null}
            <Button
              variant='outline'
              size='sm'
              disabled={isSaving}
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              size='sm'
              loading={isSaving}
              onClick={() => void handleSave()}
            >
              Save
            </Button>
          </div>
        </div>
      </Card>
      <ConfirmDialog
        open={isClearConfirmOpen}
        title='Clear notes?'
        description={`This will permanently remove the notes from ${clearTarget}.`}
        variant='destructive'
        onCancel={() => setIsClearConfirmOpen(false)}
        onContinue={() => void handleClear()}
      />
    </>
  )
}

export default NotesEditor
